import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

export const useRequestOtpTimer = ({
  isSubmitting,
  durationSeconds = 300 // Default to 5 min
}: {
  isSubmitting?: boolean;
  durationSeconds?: number;
}) => {
  const [endTime, setEndTime] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const { t } = useTranslation();

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const remainingTime = useMemo(() => {
    if (!endTime) {
      return 0;
    }
    return Math.max(0, Math.ceil((endTime - now) / 1000));
  }, [endTime, now]);

  // Update time every second
  useEffect(() => {
    if (endTime) {
      intervalRef.current = setInterval(() => {
        setNow(Date.now());
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [endTime]);

  // Stop timer when time is up
  useEffect(() => {
    if (remainingTime <= 0 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [remainingTime]);

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const remainingTimeText = useMemo(() => {
    return remainingTime > 0 ? t(`in {{time}}`, { time: formatTime(remainingTime) }) : '';
  }, [remainingTime, t]);

  const isResendDisabled = useMemo(() => {
    return isSubmitting || remainingTime > 0;
  }, [isSubmitting, remainingTime]);

  const startTimer = useCallback(() => {
    const duration = durationSeconds * 1000;
    setEndTime(Date.now() + duration);
    setNow(Date.now());
  }, [durationSeconds]);

  const resetTimer = useCallback(() => {
    setEndTime(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return {
    remainingTimeText,
    isResendDisabled,
    startTimer,
    resetTimer
  };
};
